import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, Shield, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface PolicyVersion {
  policy_type: string;
  version: string;
  summary_of_changes: string | null;
  effective_date: string;
}

interface ConsentReconfirmationDialogProps {
  open: boolean;
  outdatedPolicies: PolicyVersion[];
  onConfirm: (policyTypes: string[]) => Promise<boolean>;
}

export function ConsentReconfirmationDialog({
  open,
  outdatedPolicies,
  onConfirm,
}: ConsentReconfirmationDialogProps) {
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const allAccepted = outdatedPolicies.every(
    (policy) => accepted[policy.policy_type]
  );

  const handleConfirm = async () => {
    setLoading(true);
    const success = await onConfirm(outdatedPolicies.map((p) => p.policy_type));
    setLoading(false);

    if (success) {
      toast.success('Consent updated successfully');
    } else {
      toast.error('Failed to update consent. Please try again.');
    }
  };

  const getPolicyIcon = (type: string) => {
    if (type === 'privacy_policy') return <Shield className="h-5 w-5 text-primary" />;
    return <FileText className="h-5 w-5 text-primary" />;
  };

  const getPolicyName = (type: string) => {
    if (type === 'privacy_policy') return 'Privacy Policy';
    if (type === 'terms_and_conditions') return 'Terms and Conditions';
    return type;
  };

  const getPolicyLink = (type: string) => {
    if (type === 'privacy_policy') return '/privacy-policy';
    if (type === 'terms_and_conditions') return '/terms';
    return '#';
  };

  return (
    <Dialog open={open}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            Policy Update
          </DialogTitle>
          <DialogDescription>
            We've updated our policies. Please review and accept the changes to continue using our services.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[300px] pr-4">
          <div className="space-y-4">
            {outdatedPolicies.map((policy) => (
              <div
                key={policy.policy_type}
                className="rounded-lg border bg-muted/30 p-4 space-y-3"
              >
                <div className="flex items-start gap-3">
                  {getPolicyIcon(policy.policy_type)}
                  <div className="flex-1">
                    <h4 className="font-medium">{getPolicyName(policy.policy_type)}</h4>
                    <p className="text-sm text-muted-foreground">
                      Version {policy.version} • Effective{' '}
                      {new Date(policy.effective_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {policy.summary_of_changes && (
                  <p className="text-sm text-muted-foreground bg-background/50 rounded p-2">
                    <strong>What's changed:</strong> {policy.summary_of_changes}
                  </p>
                )}

                <div className="flex items-center justify-between">
                  <Link
                    to={getPolicyLink(policy.policy_type)}
                    target="_blank"
                    className="text-sm text-primary hover:underline"
                  >
                    Read full policy →
                  </Link>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`accept-${policy.policy_type}`}
                      checked={accepted[policy.policy_type] || false}
                      onCheckedChange={(checked) =>
                        setAccepted((prev) => ({
                          ...prev,
                          [policy.policy_type]: checked === true,
                        }))
                      }
                    />
                    <label
                      htmlFor={`accept-${policy.policy_type}`}
                      className="text-sm font-medium cursor-pointer"
                    >
                      I accept
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button
            onClick={handleConfirm}
            disabled={!allAccepted || loading}
            className="w-full"
          >
            {loading ? 'Updating...' : 'Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
